import {
  RANKS,
  TICK_DT,
  TICK_RATE,
  abilityCharges,
  abilityCooldown,
  abilityCooldownScale,
  abilityDamageScale,
  abilityDuration,
  abilityHasteMultiplier,
  abilityPower,
  attackDamageRatio,
  getHeroDef,
  heroStats,
  maxRank,
  type HeroState,
} from '@moba/shared';
import { KIT_DESCRIPTIONS, KIT_NAMES, type KitSlot } from './abilityIcons.ts';

import { t } from './i18n.ts';
export interface AbilityTooltipStat {
  label: string;
  value: string;
  detail?: string;
}

export interface AbilityTooltipData {
  title: string;
  subtitle: string;
  description: string;
  stats: AbilityTooltipStat[];
  note?: string;
}

function number(value: number, digits = 1): string {
  const rounded = Math.round(value * 10 ** digits) / 10 ** digits;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(digits);
}

function seconds(ticks: number): string {
  return `${number(ticks / TICK_RATE)} s`;
}

function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function scaleDetail(rankScale: number, adScale: number): string {
  return t('{rank} rango × {ad} AD', { rank: number(rankScale, 2), ad: number(adScale, 2) });
}

function damageDetail(base: number, rankScale: number, adScale: number): string {
  return t('{base} base × {scale}', { base: number(base), scale: scaleDetail(rankScale, adScale) });
}

function areaDetail(radius: number): string {
  return t('radio {radius} u', { radius: number(radius) });
}

function arcDetail(arc: number): string {
  return arc >= Math.PI * 1.999 ? '360°' : t('{deg}° de arco', { deg: Math.round((arc * 180) / Math.PI) });
}

export function abilityTooltipData(hero: HeroState, slot: KitSlot): AbilityTooltipData {
  const def = getHeroDef(hero.heroId);
  const stats = heroStats(hero.heroId, hero.level, hero.items);
  const title = KIT_NAMES[hero.heroId][slot];
  const description = KIT_DESCRIPTIONS[hero.heroId][slot]
    ?? (def.ranged
      ? t('Disparo a distancia; el tercer tiro del combo es más fuerte.')
      : t('Combo de tres golpes; el tercero es más fuerte.'));
  const adRatio = attackDamageRatio(hero.heroId, hero.level, hero.items);

  if (slot === 'attack') {
    const damages = def.attacks.map((attack) => attack.damage * adRatio);
    const intervals = def.attacks.map((attack) => Math.max(1, Math.round(attack.duration / stats.attackSpeed)));
    const range = def.attacks[0].range * stats.attackRange;
    return {
      title,
      subtitle: t('LMB · Ataque básico'),
      description,
      stats: [
        {
          label: damages.length > 1 ? t('Daño del combo') : t('Daño'),
          value: damages.map((damage) => number(damage)).join(' / '),
          detail: `AD × ${number(adRatio, 2)}`,
        },
        {
          label: t('Entre golpes'),
          value: intervals.map((ticks) => number(ticks / TICK_RATE, 2)).join(' / ') + ' s',
          detail: t('velocidad de ataque × {value}', { value: number(stats.attackSpeed, 2) }),
        },
        {
          label: t('Alcance'),
          value: `${number(range)} u`,
          detail: stats.attackRange !== 1 ? t('base {range} u × {mult}', { range: number(def.attacks[0].range), mult: number(stats.attackRange, 2) }) : undefined,
        },
      ],
      note: t('El daño es antes de la armadura del objetivo.'),
    };
  }

  const ability = def.abilities[slot];
  const rank = hero.ranks[slot];
  const effectiveRank = Math.max(1, rank);
  const maxRanks = slot === 'r' ? RANKS.ultimateMax : RANKS.basicMax;
  const availableRanks = maxRank(slot, hero.level);
  const rankScale = abilityDamageScale(slot, effectiveRank);
  const power = abilityPower(hero.heroId, hero.level, slot, effectiveRank, hero.items);
  const adScale = power / rankScale;
  const baseCooldown = abilityCooldown(ability);
  const cooldownScale = abilityCooldownScale(slot, effectiveRank);
  const haste = abilityHasteMultiplier(stats.abilityHaste);
  const cooldownTicks = Math.round(baseCooldown * cooldownScale * haste);
  const formula = scaleDetail(rankScale, adScale);
  const rows: AbilityTooltipStat[] = [];
  const notes: string[] = [];
  let damaging = false;
  let stunning = false;

  let rankDetail: string | undefined;
  if (rank === 0) {
    rankDetail = availableRanks === 0
      ? t('Se desbloquea al nivel {level}', { level: slot === 'r' ? 6 : 1 })
      : t('Sin puntos asignados; valores del rango 1');
  } else if (rank < availableRanks) {
    rankDetail = t('Puedes subirla con un punto');
  }
  rows.push({ label: t('Rango'), value: `${rank}/${maxRanks}`, detail: rankDetail });

  if (ability.kind === 'strike') {
    const strike = ability.strike;
    const perHit = strike.damage * power;
    const hits = strike.rehitEvery > 0
      ? 1 + Math.floor((strike.activeEnd - strike.activeStart) / strike.rehitEvery)
      : 1;
    if (perHit > 0) {
      damaging = true;
      rows.push({
        label: hits > 1 ? t('Daño por golpe') : t('Daño'),
        value: number(perHit),
        detail: damageDetail(strike.damage, rankScale, adScale),
      });
    }
    if (hits > 1) {
      rows.push({
        label: t('Golpes'),
        value: String(hits),
        detail: t('cada {every} durante {total}', { every: seconds(strike.rehitEvery), total: seconds(strike.activeEnd - strike.activeStart) }),
      });
      if (perHit > 0) {
        rows.push({ label: t('Daño total'), value: number(perHit * hits), detail: t('si todos los golpes conectan') });
      }
    }
    if (strike.sweep) {
      rows.push({ label: t('Barrido'), value: t('por la embestida'), detail: t('ancho del héroe') });
    } else if (strike.range > 0) {
      rows.push({ label: t('Alcance'), value: `${number(strike.range)} u`, detail: arcDetail(strike.arc) });
    }
    if (strike.turnToAim > 0) {
      rows.push({ label: t('Giro'), value: `${number(strike.turnToAim)} rad/s`, detail: t('hacia el cursor') });
    }
    if (strike.dashTicks > 0) {
      rows.push({
        label: t('Embestida'),
        value: `${number(strike.dashTicks * strike.dashSpeed * TICK_DT)} u`,
        detail: seconds(strike.dashTicks),
      });
    }
    if (strike.invulnTicks > 0) {
      rows.push({ label: t('Invulnerable'), value: seconds(strike.invulnTicks) });
    }
    if (strike.stun && strike.hitstun > 0) {
      stunning = true;
      rows.push({ label: t('Aturde'), value: seconds(strike.hitstun), detail: t('interrumpe y bloquea') });
    }
    if (strike.knockback > 0) {
      rows.push({ label: t('Empuje'), value: number(strike.knockback) });
    }
    if (strike.reflectProjectiles) {
      rows.push({ label: t('Refleja'), value: t('proyectiles'), detail: t('durante el lanzamiento') });
    }
    if (strike.unstoppable) {
      rows.push({ label: t('Imparable'), value: t('sí'), detail: t('no lo aturden durante la carga') });
    }
  } else if (ability.kind === 'projectile') {
    const projectile = ability.projectile;
    const perProjectile = projectile.damage * power;
    const count = Math.max(1, projectile.count);
    const explosion = projectile.explodeDamage * power;
    if (perProjectile > 0) {
      damaging = true;
      rows.push({
        label: count > 1 ? t('Daño por proyectil') : t('Daño'),
        value: number(perProjectile),
        detail: damageDetail(projectile.damage, rankScale, adScale),
      });
    }
    if (count > 1) {
      rows.push({
        label: t('Proyectiles'),
        value: String(count),
        detail: projectile.spread > 0 ? t('abanico de {deg}°', { deg: number((projectile.spread * 180) / Math.PI, 1) }) : undefined,
      });
      if (perProjectile > 0) {
        rows.push({ label: t('Daño total'), value: number(perProjectile * count), detail: t('si todos impactan') });
      }
    }
    if (explosion > 0) {
      damaging = true;
      rows.push({
        label: t('Explosión'),
        value: number(explosion),
        detail: perProjectile > 0 ? areaDetail(projectile.explodeRadius) : `${areaDetail(projectile.explodeRadius)} · ${formula}`,
      });
    }
    if (projectile.pierce > 0) {
      rows.push({
        label: t('Atraviesa'),
        value: projectile.pierce === 1 ? t('1 enemigo') : t('{count} enemigos', { count: projectile.pierce }),
        detail: t('antes de detenerse'),
      });
    }
    rows.push({
      label: t('Alcance'),
      value: `${number((projectile.speed * projectile.ttl) / TICK_RATE)} u`,
      detail: t('velocidad {value}', { value: number(projectile.speed) }),
    });
    if (projectile.stun && projectile.hitstun > 0) {
      stunning = true;
      rows.push({ label: t('Aturde'), value: seconds(projectile.hitstun) });
    }
    if (projectile.stun && explosion > 0 && projectile.explodeHitstun > 0) {
      stunning = true;
      rows.push({ label: t('Aturde (explosión)'), value: seconds(projectile.explodeHitstun) });
    }
    if (projectile.knockback > 0) {
      rows.push({ label: t('Empuje'), value: number(projectile.knockback) });
    }
    if (projectile.detonateOnRecast) {
      rows.push({ label: t('Detonar'), value: t('pulsa otra vez'), detail: t('explota antes de tiempo') });
    }
  } else if (ability.kind === 'blink') {
    const blink = ability.blink;
    rows.push({ label: t('Distancia'), value: `${number(blink.distance)} u` });
    if (blink.invulnTicks > 0) {
      rows.push({ label: t('Invulnerable'), value: seconds(blink.invulnTicks) });
    }
    if (blink.damage > 0) {
      damaging = true;
      rows.push({ label: t('Daño'), value: number(blink.damage * power), detail: damageDetail(blink.damage, rankScale, adScale) });
    }
    if (blink.radius > 0) {
      rows.push({ label: t('Radio'), value: `${number(blink.radius)} u` });
    }
  } else if (ability.kind === 'shield') {
    const shield = ability.shield;
    rows.push({ label: t('Absorbe'), value: number(shield.absorb), detail: t('daño antes de perder vida') });
    rows.push({ label: t('Duración'), value: seconds(shield.durationTicks) });
    rows.push({ label: t('Refleja'), value: percent(shield.reflect), detail: t('del daño recibido tras armadura') });
  } else if (ability.kind === 'grab') {
    const grab = ability.grab;
    damaging = true;
    rows.push({ label: t('Daño'), value: number(grab.damage * power), detail: damageDetail(grab.damage, rankScale, adScale) });
    if (grab.splashDamage > 0) {
      rows.push({ label: t('Salpicadura'), value: number(grab.splashDamage * power), detail: areaDetail(grab.splashRadius) });
    }
    if (grab.stun > 0) {
      stunning = true;
      rows.push({ label: t('Aturde'), value: seconds(grab.stun) });
    }
    rows.push({
      label: t('Embestida'),
      value: `${number(grab.dashTicks * grab.dashSpeed * TICK_DT)} u`,
      detail: t('{time} + {reach} u de alcance', { time: seconds(grab.dashTicks), reach: number(grab.reach) }),
    });
    rows.push({ label: t('En el aire'), value: seconds(grab.liftTicks) });
  } else if (ability.kind === 'execute') {
    const execute = ability.execute;
    const ratio = execute.missingHp[Math.min(execute.missingHp.length - 1, effectiveRank - 1)] ?? 0;
    damaging = true;
    rows.push({ label: t('Daño'), value: number(execute.damage * power), detail: damageDetail(execute.damage, rankScale, adScale) });
    rows.push({ label: t('Vida faltante'), value: `+${percent(ratio)}`, detail: t('de la vida que le falta al objetivo') });
    rows.push({ label: t('Tipo'), value: t('daño puro'), detail: t('ignora la armadura') });
    rows.push({ label: t('Alcance'), value: `${number(execute.range)} u`, detail: t('sólo héroes enemigos') });
    rows.push({ label: t('Impacto'), value: seconds(execute.strikeTick), detail: t('tras lanzarla') });
  }

  rows.splice(1, 0, {
    label: t('Enfriamiento'),
    value: seconds(cooldownTicks),
    detail: t('{base} base · rango × {rank} · aceleración × {haste}', { base: seconds(baseCooldown), rank: number(cooldownScale, 2), haste: number(haste, 2) }),
  });
  rows.splice(2, 0, { label: t('Lanzamiento'), value: seconds(abilityDuration(ability)) });
  const charges = abilityCharges(ability);
  if (charges > 1) {
    rows.splice(2, 0, {
      label: t('Cargas'),
      value: String(charges),
      detail: t('recupera una cada {time} mientras le quede alguna; al gastar la última, enfriamiento completo', { time: seconds(Math.round(cooldownTicks / charges)) }),
    });
  }

  if (damaging) notes.push(t('El daño es antes de la armadura del objetivo.'));
  if (stunning) notes.push(t('La tenacidad del objetivo reduce el aturdimiento.'));
  if (stats.abilityHaste > 0) notes.push(t('Aceleración de habilidad actual: {value}.', { value: number(stats.abilityHaste) }));

  return {
    title,
    subtitle: t('{key} · Rango {rank}/{max}', { key: slot.toUpperCase(), rank, max: maxRanks }),
    description,
    stats: rows,
    note: notes.length > 0 ? notes.join(' ') : undefined,
  };
}
